// src/hooks/useCapabilities.ts

import { useMemo } from 'react';
import { useAuthStore } from '../store/authStore';
import { Capability } from '../types';

export interface CapabilityInfo {
  capabilities: Capability[];

  // ===== Core Methods =====
  has: (capability: Capability) => boolean;
  hasAny: (capabilities: Capability[]) => boolean;
  hasAll: (capabilities: Capability[]) => boolean;

  // ===== Level Checks =====
  level: number;
  hasLevel: (maxLevel: number) => boolean;
  isTopLevel: boolean;          // Level 0 (Rektor/Admin)
  isUniversityLevel: boolean;   // Level 0-1 (Rektor/Warek/Senat)
  isFacultyLevel: boolean;      // Level 2 (Dekan/LPM)
  isUnitLevel: boolean;         // Level 3+ (Kaprodi/Kalem/Kabag/dll)

  // ===== Convenience Shortcuts (from capabilities) =====

  // Laporan
  canCreateLaporan: boolean;

  // Validasi
  canValidate: boolean;
  canResolveLocal: boolean;
  canEscalate: boolean;

  // RAPIM
  canManageRapim: boolean;
  canScheduleRapim: boolean;
  canMakeRapimDecision: boolean;

  // Monitoring & Tindak Lanjut
  canAccessMonitoring: boolean;
  canChangePic: boolean;
  canExtendDeadline: boolean;
  canConfirmCompletion: boolean;
  canReturnToRapim: boolean;
  canSendReminder: boolean;

  // Reporting
  canViewAllReports: boolean;
  canAccessReportBuilder: boolean;
  canArchive: boolean;

  // Admin
  canManageUsers: boolean;
  canManageMasterData: boolean;
  canManageSettings: boolean;
  canManageUnits: boolean;
  canViewActivityLog: boolean;

  // ===== Combined Checks =====
  canValidateL1: boolean;  // Level 2 atau di bawah
  canValidateL2: boolean;  // Level 1 atau di bawah
  isObserver: boolean;     // Has access_monitoring but NOT validate
}

export function useCapabilities(): CapabilityInfo {
  const capabilities = useAuthStore((s) => s.capabilities);
  const user = useAuthStore((s) => s.user);

  return useMemo(() => {
    const capSet = new Set(capabilities || []);
    const userLevel = user?.role?.level ?? 99;

    // Core methods
    const has = (cap: Capability): boolean => capSet.has(cap);
    const hasAny = (caps: Capability[]): boolean => caps.some((c) => capSet.has(c));
    const hasAll = (caps: Capability[]): boolean => caps.every((c) => capSet.has(c));

    // Level checks
    const hasLevel = (maxLevel: number): boolean => userLevel <= maxLevel;

    // Shortcuts
    const canValidate = has('validate');
    const canAccessMonitoring = has('access_monitoring');

    return {
      capabilities: capabilities || [],
      has,
      hasAny,
      hasAll,

      // Level info
      level: userLevel,
      hasLevel,
      isTopLevel: userLevel === 0,
      isUniversityLevel: userLevel <= 1,
      isFacultyLevel: userLevel === 2,
      isUnitLevel: userLevel >= 3,

      // Capability shortcuts
      canCreateLaporan: has('create_laporan'),
      canValidate,
      canResolveLocal: has('resolve_local'),
      canEscalate: has('escalate'),
      canManageRapim: has('manage_rapim'),
      canScheduleRapim: has('schedule_rapim'),
      canMakeRapimDecision: has('make_rapim_decision'),
      canAccessMonitoring,
      canChangePic: has('change_pic'),
      canExtendDeadline: has('extend_deadline'),
      canConfirmCompletion: has('confirm_completion'),
      canReturnToRapim: has('return_to_rapim'),
      canSendReminder: has('send_reminder'),
      canViewAllReports: has('view_all_reports'),
      canAccessReportBuilder: has('access_report_builder'),
      canArchive: has('archive'),
      canManageUsers: has('manage_users'),
      canManageMasterData: has('manage_master_data'),
      canManageSettings: has('manage_settings'),
      canManageUnits: has('manage_units'),
      canViewActivityLog: has('view_activity_log'),

      // Combined checks
      canValidateL1: canValidate && hasLevel(2),  // Dekan level or above
      canValidateL2: canValidate && hasLevel(1),  // Warek level or above
      isObserver: canAccessMonitoring && !canValidate,
    };
  }, [capabilities, user?.role?.level]);
}
